// hc-pdp-shipping-helper.js
// <hc-pdp-shipping-helper> — contextual free-shipping message above PDP ATC.
// Subscribes to cart:change, fetches /cart.js, computes eligible subtotal
// (excludes LTL line items), and renders one of four states:
//
//   1. This product is LTL → "Ships separately by freight..."
//   2. (cart_eligible_subtotal + this_price) >= threshold → "Adding this unlocks free shipping"
//   3. cart_eligible_subtotal >= threshold → "Cart qualifies for free shipping"
//   4. else → "Add this — you'll be $X.XX from free shipping"

(function () {
  'use strict';

  if (!window.customElements) return;
  if (customElements.get('hc-pdp-shipping-helper')) return;

  var locale = (window.Shopify && Shopify.locale) || 'en-US';
  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
  var formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  var noDecimalFormatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  function eligibleSubtotal(cart) {
    var ltlProducts = new Set(window.HC_LTL_PRODUCT_IDS || []);
    var ltlVariants = new Set(window.HC_LTL_VARIANT_IDS || []);
    var sum = 0;
    (cart.items || []).forEach(function (item) {
      if (!item.requires_shipping) return;
      if (ltlProducts.has(item.product_id)) return;
      if (ltlVariants.has(item.variant_id)) return;
      sum += item.final_line_price || 0;
    });
    var discount = (cart.cart_level_discount_applications || []).reduce(function (s, d) {
      return s + (d.total_allocated_amount || 0);
    }, 0);
    return Math.max(0, sum - discount);
  }

  class HcPdpShippingHelper extends HTMLElement {
    connectedCallback() {
      this._msg = this.querySelector('.hc-pdp-ship__msg');
      this._price = parseInt(this.getAttribute('data-product-price'), 10) || 0;
      this._isLtl = this.getAttribute('data-product-is-ltl') === 'true';
      this._threshold = parseInt(this.getAttribute('data-threshold'), 10) || 9900;
      this._lastEligibleCents = 0;

      // LTL: server already wrote the static message; nothing to compute.
      if (this._isLtl) return;

      this._onCartChange = this._onCartChange.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      document.addEventListener('cart:change', this._onCartChange);

      // Scope variant:change to this product's container so a related-product
      // form on the same page can't hijack the main PDP helper.
      this._scope = this.closest('.product-info') || this.closest('product-info') || document;
      this._scope.addEventListener('variant:change', this._onVariantChange);

      // Initial fetch.
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          this._lastEligibleCents = eligibleSubtotal(cart);
          this._render(this._lastEligibleCents);
        }.bind(this))
        .catch(function () { /* swallow */ });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChange);
      if (this._scope) {
        this._scope.removeEventListener('variant:change', this._onVariantChange);
      }
    }

    _onCartChange(e) {
      var cart = e && e.detail && e.detail.cart;
      if (!cart) return;
      this._lastEligibleCents = eligibleSubtotal(cart);
      this._render(this._lastEligibleCents);
    }

    _onVariantChange(e) {
      var variant = e && e.detail && e.detail.variant;
      if (!variant || typeof variant.price !== 'number') return;
      this._price = variant.price;
      this._render(this._lastEligibleCents);
    }

    _render(eligibleCents) {
      if (!this._msg) return;

      var threshold = this._threshold;
      var price = this._price;

      // State A: cart already at/above threshold (without this product needed)
      if (eligibleCents >= threshold) {
        this._msg.innerHTML = this._icon('check') +
          ' Your cart qualifies for <span class="hc-pdp-ship__accent">free shipping</span>.';
        this.setAttribute('data-state', 'qualified');
        return;
      }

      // State B: adding this product (1 qty) would cross the threshold
      if (eligibleCents + price >= threshold) {
        this._msg.innerHTML = this._icon('check') +
          ' Adding this unlocks <span class="hc-pdp-ship__accent">free shipping</span>.';
        this.setAttribute('data-state', 'unlocks');
        return;
      }

      // State C: still below threshold even after adding this
      var remaining = threshold - (eligibleCents + price);
      var remainingLabel = formatter.format(remaining / 100);
      this._msg.innerHTML = this._icon('truck') +
        ' Add this and you’ll be <span class="hc-pdp-ship__accent">' + remainingLabel +
        '</span> from free shipping (' + noDecimalFormatter.format(threshold / 100) + ').';
      this.setAttribute('data-state', 'progress');
    }

    _icon(name) {
      if (name === 'check') {
        return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
      }
      // truck
      return '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
    }
  }

  customElements.define('hc-pdp-shipping-helper', HcPdpShippingHelper);
})();
